import { createContext, useContext, useMemo, useState, type ReactNode } from "react";
import {
  CARGOS_INICIALES,
  FESTIVOS_INICIALES,
  PRODUCTOS_INICIALES,
  paramsDeProducto,
  copiarCargosDeProducto,
  r2,
  SOLICITUD_DEMO,
  VALOR_UDI_DEFAULT,
  calcularCondiciones,
  estadoDeCuenta,
  motorReal,
  pagosDemo,
  tablaProgramada,
  type Cargo,
  type Pago,
  type PenaManual,
  type Producto,
  type Solicitud,
} from "./operativo";
import {
  CONFIG_REVOLVENTE_DEFAULT,
  escenarioDemoRevolvente,
  simularRevolvente,
  type ConfigRevolvente,
  type DisposicionRev,
  type PagoRev,
  type PenaRev,
} from "./revolvente";

const SOLICITUD_INICIAL: Solicitud = {
  productoId: "pyme",
  monto: 350000,
  plazo: 12,
  fechaDesembolso: "2026-01-15",
  diaPago: 15,
  garantia: { descripcion: "", valor: 0, fechaFactura: "", tipo: "Prenda sobre maquinaria" },
  params: paramsDeProducto(PRODUCTOS_INICIALES.find((p) => p.id === "pyme")!),
  avalNombre: "",
};

function useOperativoState() {
  const [productos, setProductos] = useState<Producto[]>(PRODUCTOS_INICIALES);
  const [valorUdi, setValorUdiState] = useState<number>(VALOR_UDI_DEFAULT);
  const [cargos, setCargos] = useState<Cargo[]>(CARGOS_INICIALES);
  const [festivos, setFestivos] = useState<string[]>(FESTIVOS_INICIALES);
  const [solicitud, setSolicitud] = useState<Solicitud>(SOLICITUD_INICIAL);
  /** Copia editable de la plantilla del producto, propia de la solicitud en curso. */
  const [cargosCredito, setCargosCredito] = useState<Cargo[]>(
    copiarCargosDeProducto(CARGOS_INICIALES, SOLICITUD_INICIAL.productoId),
  );
  const [pagos, setPagos] = useState<Pago[]>([]);
  const [penas, setPenas] = useState<PenaManual[]>([]);
  const [hoy, setHoy] = useState<string>(new Date().toISOString().slice(0, 10));

  /* ------------------------- Línea revolvente (módulo) ---------------------- */
  const prodRev = PRODUCTOS_INICIALES.find((p) => p.esRevolvente);
  const [configRev, setConfigRev] = useState<ConfigRevolvente>({
    ...CONFIG_REVOLVENTE_DEFAULT,
    tasaAnual: prodRev?.tasaAnual ?? CONFIG_REVOLVENTE_DEFAULT.tasaAnual,
    tasaMoratoria: r2((prodRev?.tasaAnual ?? 70) * (prodRev?.factorMora ?? 2)),
  });
  const [disposiciones, setDisposiciones] = useState<DisposicionRev[]>([]);
  const [pagosRev, setPagosRev] = useState<PagoRev[]>([]);
  const [penasRev, setPenasRev] = useState<PenaRev[]>([]);
  const [hoyRev, setHoyRev] = useState<string>(CONFIG_REVOLVENTE_DEFAULT.fechaApertura);

  /** El valor UDI es global: se propaga al crédito en curso y a los defaults de cada producto. */
  const setValorUdi = (v: number) => {
    setValorUdiState(v);
    setProductos((prev) => prev.map((p) => ({ ...p, params: { ...p.params, valorUdi: v } })));
    setSolicitud((prev) => ({ ...prev, params: { ...prev.params, valorUdi: v } }));
  };

  const restaurarFabrica = () => {
    setProductos(PRODUCTOS_INICIALES);
    setValorUdiState(VALOR_UDI_DEFAULT);
  };

  const producto = productos.find((p) => p.id === solicitud.productoId) ?? productos[0]!;

  /** Cambia el producto de la solicitud y precarga su plantilla de cargos como copia. */
  const cambiarProductoSolicitud = (productoId: string) => {
    const prod = productos.find((p) => p.id === productoId);
    setSolicitud((prev) => ({
      ...prev,
      productoId,
      ...(prod ? { params: paramsDeProducto(prod) } : {}),
    }));
    setCargosCredito(copiarCargosDeProducto(cargos, productoId));
  };

  /** Vuelve a la plantilla original del producto, descartando la edición por crédito. */
  const restaurarCargosDelProducto = () =>
    setCargosCredito(copiarCargosDeProducto(cargos, solicitud.productoId));

  const condiciones = useMemo(
    () => calcularCondiciones(producto, cargosCredito, solicitud),
    [producto, cargosCredito, solicitud],
  );
  const programada = useMemo(
    () => tablaProgramada(producto, condiciones, solicitud, festivos),
    [producto, condiciones, solicitud, festivos],
  );
  const real = useMemo(
    () => motorReal(producto, condiciones, solicitud, programada, pagos, penas, hoy),
    [producto, condiciones, solicitud, programada, pagos, penas, hoy],
  );
  const estado = useMemo(
    () => estadoDeCuenta(producto, condiciones, real, pagos, hoy),
    [producto, condiciones, real, pagos, hoy],
  );

  const estadoRev = useMemo(
    () => simularRevolvente(configRev, disposiciones, pagosRev, penasRev, hoyRev, festivos),
    [configRev, disposiciones, pagosRev, penasRev, hoyRev, festivos],
  );

  const cargarDemoRevolvente = () => {
    const d = escenarioDemoRevolvente();
    setConfigRev(d.config);
    setDisposiciones(d.disposiciones);
    setPagosRev(d.pagos);
    setPenasRev(d.penas);
    setHoyRev(d.hoy);
  };

  const limpiarRevolvente = () => {
    setDisposiciones([]);
    setPagosRev([]);
    setPenasRev([]);
    setHoyRev(configRev.fechaApertura);
  };

  /** Avanza la línea de tiempo simulada al siguiente corte (dispara el corte). */
  const ejecutarCorte = () => setHoyRev(estadoRev.proximoCorte);

  const cargarDemo = () => {
    setProductos(PRODUCTOS_INICIALES);
    setValorUdiState(VALOR_UDI_DEFAULT);
    setCargos(CARGOS_INICIALES);
    setFestivos(FESTIVOS_INICIALES);
    setSolicitud(SOLICITUD_DEMO);
    setCargosCredito(copiarCargosDeProducto(CARGOS_INICIALES, SOLICITUD_DEMO.productoId));
    const prod = PRODUCTOS_INICIALES.find((p) => p.id === SOLICITUD_DEMO.productoId)!;
    setPagos(pagosDemo(prod, CARGOS_INICIALES, SOLICITUD_DEMO, FESTIVOS_INICIALES));
    setPenas([]);
    setHoy("2027-02-01");
  };

  return {
    productos,
    setProductos,
    valorUdi,
    setValorUdi,
    restaurarFabrica,
    cargos,
    setCargos,
    cargosCredito,
    setCargosCredito,
    restaurarCargosDelProducto,
    cambiarProductoSolicitud,
    festivos,
    setFestivos,
    solicitud,
    setSolicitud,
    pagos,
    setPagos,
    penas,
    setPenas,
    hoy,
    setHoy,
    producto,
    condiciones,
    programada,
    real,
    estado,
    cargarDemo,
    configRev,
    setConfigRev,
    disposiciones,
    setDisposiciones,
    pagosRev,
    setPagosRev,
    penasRev,
    setPenasRev,
    hoyRev,
    setHoyRev,
    estadoRev,
    cargarDemoRevolvente,
    limpiarRevolvente,
    ejecutarCorte,
    limpiar: () => {
      setPagos([]);
      setPenas([]);
    },
  };
}

type Ctx = ReturnType<typeof useOperativoState>;
const OperativoContext = createContext<Ctx | null>(null);

export function OperativoProvider({ children }: { children: ReactNode }) {
  const value = useOperativoState();
  return <OperativoContext.Provider value={value}>{children}</OperativoContext.Provider>;
}

export function useOperativo(): Ctx {
  const ctx = useContext(OperativoContext);
  if (!ctx) throw new Error("useOperativo debe usarse dentro de OperativoProvider");
  return ctx;
}
